/**
 * Time Off API Contract
 *
 * Defines the oRPC contract for Time Off - employee time off requests,
 * approvals, and management.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateTimeOffRequestInputSchema, CreateTimeOffInputSchema, UpdateTimeOffInputSchema, TimeOffResponseSchema, } from "@hourly/api/timeOff";
import { TimeOffWithApprovalRequestResponseSchema } from "@hourly/api/approvalRequest";
// ============================================
// Time Off Request Contracts (with approval)
// ============================================
/**
 * POST /time-off/requests
 * Create time off request with approval workflow
 * Note: requesterId and companyId come from JWT, not request body
 */
const createTimeOffRequest = oc
    .route({
    method: "POST",
    path: "/time-off/requests",
    summary: "Create time off request with approval workflow",
    tags: ["TimeOff"],
    successStatus: 201,
})
    .input(CreateTimeOffRequestInputSchema)
    .output(TimeOffWithApprovalRequestResponseSchema);
// ============================================
// Direct Time Off Contracts (admin override)
// ============================================
/**
 * POST /time-off
 * Create time off directly (admin override, no approval workflow)
 * Note: companyId comes from JWT, not request body
 */
const createTimeOff = oc
    .route({
    method: "POST",
    path: "/time-off",
    summary: "Create time off directly (admin override)",
    tags: ["TimeOff"],
    successStatus: 201,
})
    .input(CreateTimeOffInputSchema)
    .output(TimeOffResponseSchema);
// ============================================
// Query Contracts
// ============================================
/**
 * GET /time-off/{id}
 * Get time off by ID
 */
const getTimeOff = oc
    .route({
    method: "GET",
    path: "/time-off/{id}",
    summary: "Get time off by ID",
    tags: ["TimeOff"],
})
    .input(z.object({ id: z.uuid() }))
    .output(TimeOffResponseSchema);
/**
 * GET /time-off/employee/{employeeId}
 * Get all time off for an employee
 * Query params: startDate, endDate (optional date range filter)
 */
const getTimeOffByEmployee = oc
    .route({
    method: "GET",
    path: "/time-off/employee/{employeeId}",
    summary: "Get all time off for an employee",
    tags: ["TimeOff"],
})
    .input(z.object({
    employeeId: z.uuid(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
}))
    .output(z.array(TimeOffResponseSchema));
/**
 * GET /time-off/company
 * Get all time off for the current company (companyId from JWT)
 * Query params: startDate, endDate (optional date range filter)
 */
const getTimeOffByCompany = oc
    .route({
    method: "GET",
    path: "/time-off/company",
    summary: "Get all time off for the current company",
    tags: ["TimeOff"],
})
    .input(z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
}))
    .output(z.array(TimeOffResponseSchema));
// ============================================
// Update/Delete Contracts
// ============================================
/**
 * PATCH /time-off/{id}
 * Update existing time off
 */
const updateTimeOff = oc
    .route({
    method: "PATCH",
    path: "/time-off/{id}",
    summary: "Update time off",
    tags: ["TimeOff"],
})
    .input(z.object({ id: z.uuid() }).merge(UpdateTimeOffInputSchema))
    .output(TimeOffResponseSchema);
/**
 * DELETE /time-off/{id}
 * Delete time off
 */
const deleteTimeOff = oc
    .route({
    method: "DELETE",
    path: "/time-off/{id}",
    summary: "Delete time off",
    tags: ["TimeOff"],
    successStatus: 200,
})
    .input(z.object({ id: z.uuid() }))
    .output(TimeOffResponseSchema);
// ============================================
// Export Contract
// ============================================
export const timeOffContract = {
    createRequest: createTimeOffRequest,
    create: createTimeOff,
    get: getTimeOff,
    getByEmployee: getTimeOffByEmployee,
    getByCompany: getTimeOffByCompany,
    update: updateTimeOff,
    delete: deleteTimeOff,
};
//# sourceMappingURL=timeOff.js.map